import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/lib/api";
import { toast } from "sonner";

function display(value) {
  return value === null || value === undefined ? "-" : value;
}

export default function TeacherStudentDetail() {
  const { classId, studentId } = useParams();
  const [student, setStudent] = useState(null);
  const [artifacts, setArtifacts] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    Promise.all([
      api.get(`/teacher/classes/${classId}/students/${studentId}`),
      api.get(`/teacher/classes/${classId}/students/${studentId}/artifacts`),
    ])
      .then(([studentRes, artifactsRes]) => {
        setStudent(studentRes.data);
        setArtifacts(artifactsRes.data);
      })
      .catch((e) => setError(e?.response?.data?.detail || "Could not load student."));
  }, [classId, studentId]);

  const downloadWritingPdf = async (writingId) => {
    try {
      const { data } = await api.get(`/teacher/classes/${classId}/students/${studentId}/writing/${writingId}/report.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `thoughtsmith-writing-${writingId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Could not export writing feedback PDF.");
    }
  };

  return (
    <div className="min-h-screen bg-forge-900 text-forge-100">
      <Header />
      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-12 sm:py-16">
        <Link to={`/teacher/classes/${classId}`} className="text-xs uppercase tracking-[0.25em] text-forge-100/45 hover:text-ember-400">Back to Class</Link>
        {error ? (
          <div className="mt-10 border border-red-500/30 bg-red-500/10 p-5 text-sm text-red-200">{error}</div>
        ) : !student ? (
          <div className="mt-10 text-sm tracking-[0.3em] uppercase text-ember-500 animate-pulse">Loading student...</div>
        ) : (
          <>
            <div className="mt-10 mb-10">
              <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-3">Student Detail</div>
              <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mb-2">{student.name}</h1>
              <p className="text-sm text-forge-100/55">{student.email}</p>
            </div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-6 mb-12">
              <Stat label="Readings" value={student.reading_completed_count} />
              <Stat label="Debates" value={student.debate_completed_count} />
              <Stat label="Journals" value={student.journal_completed_count} />
              <Stat label="Avg Score" value={display(student.avg_reasoning_score)} />
              <Stat label="Reports" value={student.report_cards_completed} />
            </div>

            <section className="grid lg:grid-cols-2 gap-8 mb-12">
              <Panel title="Recent Readings">
                {student.recent_readings?.length ? (
                  <ul className="divide-y divide-white/5">
                    {student.recent_readings.map((reading) => (
                      <li key={reading.session_id} className="py-3">
                        <div className="font-serif text-lg">{reading.title}</div>
                        <div className="text-xs text-forge-100/45">{reading.completed_at?.slice(0, 10) || "-"}</div>
                      </li>
                    ))}
                  </ul>
                ) : <Empty>No completed readings yet.</Empty>}
              </Panel>

              <Panel title="Completed Debates">
                {student.completed_debates?.length ? (
                  <ul className="divide-y divide-white/5">
                    {student.completed_debates.map((debate) => (
                      <li key={debate.debate_id} className="py-3 flex justify-between gap-4">
                        <div>
                          <div className="font-serif text-lg">{debate.reading_title}</div>
                          <div className="text-xs text-forge-100/45">{debate.completed_at?.slice(0, 10) || "-"}</div>
                          <Link
                            to={`/teacher/classes/${classId}/students/${studentId}/debates/${debate.debate_id}/report`}
                            className="inline-block mt-2 text-[10px] uppercase tracking-[0.25em] text-ember-400 hover:text-ember-300"
                          >
                            View Debate Report
                          </Link>
                        </div>
                        <div className="text-right">
                          <div className="font-serif text-2xl text-ember-400">{display(debate.overall_score)}</div>
                          <div className="text-[10px] uppercase tracking-[0.2em] text-forge-100/40">{debate.reasoning_level || "Report"}</div>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : <Empty>No completed debates yet.</Empty>}
              </Panel>
            </section>

            <section className="grid lg:grid-cols-2 gap-8 mb-12">
              <Panel title="Report Card Trend">
                {student.report_card_trend?.length ? (
                  <div className="space-y-3">
                    {student.report_card_trend.map((point) => (
                      <div key={point.debate_id} className="flex items-center gap-4">
                        <div className="w-24 text-xs text-forge-100/45">{point.label}</div>
                        <div className="flex-1 h-2 bg-white/8">
                          <div className="h-full bg-ember-500" style={{ width: `${point.overall_score}%` }} />
                        </div>
                        <div className="w-10 text-right font-mono text-xs text-forge-100/60">{point.overall_score}</div>
                      </div>
                    ))}
                  </div>
                ) : <Empty>No report cards yet.</Empty>}
              </Panel>

              <Panel title="Journal Completion">
                <div className="font-serif text-5xl text-forge-100 mb-3">{student.journal_status?.completed_count ?? 0}</div>
                <p className="text-sm text-forge-100/55 mb-6">Completed journal entries. Entry text is private in the Phase 1 teacher dashboard.</p>
                {student.journal_status?.recent_entries?.length ? (
                  <ul className="divide-y divide-white/5">
                    {student.journal_status.recent_entries.map((entry) => (
                      <li key={`${entry.session_id}-${entry.updated_at}`} className="py-3 flex justify-between gap-4">
                        <span className="font-serif text-lg">{entry.title}</span>
                        <span className="text-xs text-forge-100/45">{entry.updated_at?.slice(0, 10) || "-"}</span>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </Panel>
            </section>

            <section className="grid lg:grid-cols-2 gap-8">
              <Panel title="Saved Reflections">
                {artifacts?.reflections?.length ? (
                  <div className="space-y-5">
                    {artifacts.reflections.slice(0, 5).map((reflection) => (
                      <div key={`${reflection.session_id}-${reflection.updated_at}`} className="border-l-2 border-white/10 pl-4">
                        <div className="font-serif text-lg mb-1">{reflection.title || "Reading reflection"}</div>
                        <div className="text-xs text-forge-100/40 mb-3">{reflection.updated_at?.slice(0, 10) || "-"}</div>
                        <div className="space-y-3">
                          {Object.entries(reflection.answers || {}).map(([key, value]) => (
                            value ? (
                              <div key={key}>
                                <div className="text-[10px] uppercase tracking-[0.25em] text-forge-100/40 mb-1">{key}</div>
                                <p className="font-reading text-sm leading-relaxed text-forge-100/80">{value}</p>
                              </div>
                            ) : null
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : <Empty>No saved reflections yet.</Empty>}
              </Panel>

              <Panel title="Debate Responses">
                {artifacts?.debates?.length ? (
                  <div className="space-y-5">
                    {artifacts.debates.slice(0, 5).map((debate) => (
                      <div key={debate.debate_id} className="border-l-2 border-white/10 pl-4">
                        <div className="font-serif text-lg mb-1">{debate.reading_title}</div>
                        <div className="text-xs text-forge-100/40 mb-3">{debate.status} · {debate.completed_at?.slice(0, 10) || debate.started_at?.slice(0, 10) || "-"}</div>
                        <div className="space-y-3">
                          {(debate.transcript || []).map((turn, index) => (
                            <div key={`${debate.debate_id}-${turn.label}-${index}`}>
                              <div className="text-[10px] uppercase tracking-[0.25em] text-forge-100/40 mb-1">{turn.label}</div>
                              <p className="font-reading text-sm leading-relaxed text-forge-100/80 whitespace-pre-wrap">{turn.text}</p>
                            </div>
                          ))}
                        </div>
                        {debate.report_available && (
                          <Link
                            to={`/teacher/classes/${classId}/students/${studentId}/debates/${debate.debate_id}/report`}
                            className="inline-block mt-3 text-[10px] uppercase tracking-[0.25em] text-ember-400 hover:text-ember-300"
                          >
                            View Full Report
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                ) : <Empty>No debate responses yet.</Empty>}
              </Panel>
            </section>

            <section className="mt-8">
              <Panel title="Writing Mentor Artifacts">
                {artifacts?.writing?.length ? (
                  <div className="space-y-6">
                    {artifacts.writing.slice(0, 8).map((item) => (
                      <div key={item.writing_id} className="border-l-2 border-white/10 pl-4">
                        <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
                          <div>
                            <div className="font-serif text-lg mb-1">{item.mode_label} feedback</div>
                            <div className="text-xs text-forge-100/40">
                              {item.created_at?.slice(0, 10) || "-"} · {item.draft_word_count || 0} words
                              {item.fallback_used ? " · fallback used" : ""}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => downloadWritingPdf(item.writing_id)}
                            className="text-[10px] uppercase tracking-[0.25em] text-ember-400 hover:text-ember-300"
                          >
                            Export PDF
                          </button>
                        </div>
                        <p className="font-reading text-sm leading-relaxed text-forge-100/70 mb-4 whitespace-pre-wrap">{item.draft_excerpt}</p>
                        <div className="grid md:grid-cols-2 gap-5">
                          <ArtifactList title="Clarifying Questions" items={item.feedback?.clarifying_questions} />
                          <ArtifactList title="Revision Priorities" items={item.feedback?.revision_priorities} />
                          <ArtifactText title="Strongest Idea" text={item.feedback?.strongest_idea} />
                          <ArtifactText title="Next Revision Task" text={item.feedback?.next_revision_task} />
                        </div>
                        <TeacherPressurePoints points={item.feedback?.intellectual_pressure_points} />
                      </div>
                    ))}
                  </div>
                ) : <Empty>No Writing Mentor submissions yet.</Empty>}
              </Panel>
            </section>
          </>
        )}
      </main>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-white/10 p-6">
      <div className="text-[10px] uppercase tracking-[0.3em] text-forge-100/45 mb-3">{label}</div>
      <div className="font-serif text-4xl text-forge-100">{value ?? 0}</div>
    </div>
  );
}

function Panel({ title, children }) {
  return (
    <section className="border border-white/10 p-6">
      <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-5">{title}</div>
      {children}
    </section>
  );
}

function Empty({ children }) {
  return <div className="border border-dashed border-white/10 p-6 text-sm text-forge-100/45 text-center">{children}</div>;
}

function ArtifactList({ title, items }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-forge-100/40 mb-2">{title}</div>
      <ul className="space-y-2">
        {(items || []).slice(0, 4).map((item, index) => (
          <li key={`${title}-${index}`} className="font-reading text-sm leading-relaxed text-forge-100/75">- {item}</li>
        ))}
      </ul>
    </div>
  );
}

function ArtifactText({ title, text }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-[0.25em] text-forge-100/40 mb-2">{title}</div>
      <p className="font-reading text-sm leading-relaxed text-forge-100/75">{text || "-"}</p>
    </div>
  );
}

function TeacherPressurePoints({ points }) {
  if (!points?.length) return null;
  return (
    <div className="mt-5 border-t border-white/10 pt-5">
      <div className="text-[10px] uppercase tracking-[0.25em] text-ember-500 mb-3">Intellectual Pressure Points</div>
      <div className="space-y-4">
        {points.slice(0, 7).map((point, index) => (
          <div key={`${point.idea}-${index}`} className="border border-white/10 p-4 bg-forge-800/30">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
              <div className="font-serif text-base text-forge-100">{point.idea}</div>
              {point.pressure_level ? (
                <span className="text-[10px] uppercase tracking-[0.18em] text-ember-300 border border-ember-500/30 px-2 py-1">
                  {point.pressure_level}
                </span>
              ) : null}
            </div>
            <p className="font-reading text-sm leading-relaxed text-forge-100/70 mb-2">{point.why_it_matters}</p>
            <p className="font-reading text-sm leading-relaxed text-gold-300/90">{point.question}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
